import { css } from '@emotion/react';
import { keyframes, styled } from '@mui/material/styles';
import { Box, Button, FormControlLabel } from '@mui/material';
import {
  APPROVAL_HEADER_CHIP,
  APPROVAL_STATUS_CHIP,
  APPROVAL_STATUS_COLOR,
} from '@/components/Approval/_tokens/statusColors';
import { CardRowChrome } from '@/components/Approval/RelatedApproval/styles';
import { infoSectionTitleStyles } from '@/components/Approval/@components/InfoSection';
import { HOVER_SCROLLBAR_ATTR } from '@/components/Approval/@hooks/useHoverScrollbar';
import type { StatusBadgeTone } from './statusMapping';
import {
  appDarkInvertedPaperCss,
  appDarkRichContentCss,
} from '@/styles/appDarkMode';

// ===== Stage 1: 단일 컬럼 셸 =====

/** 문서 컨테이너 안쪽 여백. 작성 화면이 조회와 글 폭을 맞출 때 참조 */
export const VIEW_CONTAINER_PADDING = 28;

/** 인쇄 창에서 종이 폭을 넘는 가로 스크롤 영역에 붙이는 속성 */
export const PRINT_FIT_ATTR = 'data-print-fit';

/**
 * 전역 스타일이 스크롤바를 모두 숨겨 마우스로는 넓은 표를 옆으로 밀 수 없음 — 품의 작성·조회 화면만 가로 스크롤바를 되살린다.
 * 작성 화면 Container 도 같이 씀
 */
export const approvalHorizontalScrollbarStyles = {
  '& *::-webkit-scrollbar:horizontal': { display: 'block', height: 8 },
  '& *::-webkit-scrollbar-track:horizontal': { background: 'transparent' },
  // 평소엔 막대를 숨기고 마우스를 올린 영역만 보여 준다(useHoverScrollbar 가 속성을 붙임)
  '& *::-webkit-scrollbar-thumb:horizontal': {
    backgroundColor: 'transparent',
    borderRadius: 4,
  },
  [`& [${HOVER_SCROLLBAR_ATTR}]::-webkit-scrollbar-thumb:horizontal`]: {
    backgroundColor: 'var(--dk-line-strong, #c4c8cd)',
  },
  // 에디터 툴바는 바 없이 휠·손가락으로 민다
  '& .toolbar::-webkit-scrollbar:horizontal': { display: 'none' },
  // 종이에선 옆으로 밀 수 없으니 종이 폭을 넘는 표만 줄인다(인쇄 창이 PRINT_FIT_ATTR 를 붙임)
  '@media print': {
    '& *::-webkit-scrollbar:horizontal': { display: 'none' },
    [`& [${PRINT_FIT_ATTR}] table, & table[${PRINT_FIT_ATTR}]`]: {
      display: 'table !important',
      width: 'auto !important',
      minWidth: '0 !important',
      tableLayout: 'auto !important',
    },
    [`& [${PRINT_FIT_ATTR}] col, & table[${PRINT_FIT_ATTR}] col`]: {
      width: 'auto !important',
    },
    [`& [${PRINT_FIT_ATTR}] :is(th, td), & table[${PRINT_FIT_ATTR}] :is(th, td)`]:
      {
        padding: '4px 6px !important',
        fontSize: '12px !important',
        lineHeight: '16px !important',
      },
  },
} as const;

/* 최상위 컨테이너 */
export const Container = styled(Box)(() => ({
  width: 'calc(100% - 2px)',
  maxWidth: 1920,
  height: 'max-content',
  margin: '0 auto',
  padding: VIEW_CONTAINER_PADDING,
  // 모바일 좌우는 작성 화면과 같은 16px. 기준은 조회 메뉴·번역 창과 같은 768px, 인쇄물은 제외
  '@media screen and (max-width: 768px)': {
    paddingLeft: 16,
    paddingRight: 16,
  },
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--dk-bg, #fefefe)',
  boxShadow: '0 0 1px #888',
  fontFamily: 'Pretendard, sans-serif',
  color: 'var(--dk-text, #1d2025)',
  boxSizing: 'border-box',
  // 전자결재 굵은 글씨는 600 — 레거시 700 은 폰트에 600 이 없던 시절 값
  '& strong, & b': { fontWeight: 600 },
  ...approvalHorizontalScrollbarStyles,
}));

/** 상단 컬러 바 색 — 문서 상태 tone 매핑 */
const TOP_STATUS_BAR_COLOR: Record<StatusBadgeTone, string> =
  APPROVAL_STATUS_COLOR;

export const TopStatusBarRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone: StatusBadgeTone }>(({ tone }) => ({
  height: 10,
  width: 'calc(100% - 30px)',
  maxWidth: 1890,
  margin: '0 auto',
  backgroundColor: TOP_STATUS_BAR_COLOR[tone] ?? '#888d96',
}));

// ===== 로딩/에러 상태 =====

/* 로딩/에러 메시지를 가운데에 표시하는 fallback 컨테이너 */
export const StateFallback = styled(Box)`
  height: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  box-sizing: border-box;
  color: var(--dk-text-secondary, #888d96);
  font-size: 14px;
  line-height: 20px;
`;

// ===== Stage 2 재배치 예정 — 유지 styled =====
// 현재 미마운트여도 2단계 재배치 baseline 으로 보존.

// ----- StatusBadgeBase -----

const TONE_PALETTE: Record<
  StatusBadgeTone,
  { color: string; bg: string; border: string }
> = {
  neutral: {
    color: 'var(--dk-text-secondary, #888d96)',
    bg: 'var(--dk-bg-muted, #f3f5f6)',
    border: 'var(--dk-line, #e7e9ec)',
  },
  info: {
    color: 'var(--dk-accent, #0066cc)',
    bg: '#e0edf9',
    border: 'var(--dk-accent-line, #c2daf3)',
  },
  warning: { color: '#a8650a', bg: '#fef7e0', border: '#f5d27a' },
  danger: {
    color: '#d63842',
    bg: 'var(--dk-danger-soft, #fef1f2)',
    border: 'var(--dk-danger-line, #fdd5d9)',
  },
  success: { color: '#226d40', bg: '#ecfdf5', border: '#a7f3d0' },
};

type BadgePalette = { color: string; bg: string; border: string };

/**
 * variant='tag': 연관품의 상태 태그 — paletteOverride 로 Approval_Status 토큰 주입(TONE_PALETTE 와 hex 다름).
 * default: 결재라인 카드 기존 사양 (ApproverStatusBadge 경로 불변).
 */
export const StatusBadgeBaseRoot = styled(Box, {
  shouldForwardProp: (p) =>
    p !== 'tone' && p !== 'variant' && p !== 'paletteOverride',
})<{
  tone: StatusBadgeTone;
  variant?: 'default' | 'tag';
  paletteOverride?: BadgePalette;
}>(({ tone, variant = 'default', paletteOverride }) => {
  const palette = paletteOverride ?? TONE_PALETTE[tone];
  const isTag = variant === 'tag';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: isTag ? 2 : 4,
    // tag 는 작성자명 바로 앞에 오므로 자체 우측 여백으로 간격 확보(컨테이너 gap 만으론 부족).
    ...(isTag
      ? {
          padding: '2px 8px',
          marginRight: 6,
          // prefix 아이콘만 좌측으로 당겨 기존 with-icon 간격 유지. prefix=null 딱지는 svg 가 없어 대칭 8px.
          '& > svg': { marginLeft: -4 },
        }
      : { height: 24, padding: '0 8px' }),
    borderRadius: 4,
    border: `1px solid ${palette.border}`,
    backgroundColor: palette.bg,
    color: palette.color,
    fontFamily: 'Pretendard, sans-serif',
    fontSize: isTag ? 12 : 14,
    fontWeight: 500,
    lineHeight: isTag ? '18px' : '16px',
    whiteSpace: 'nowrap',
    '& svg': {
      width: isTag ? 16 : 12,
      height: isTag ? 16 : 12,
    },
  };
});

// size/line-height 는 root 가 결정 — variant 별 글자 크기를 라벨이 따라가도록 inherit.
export const StatusBadgeLabel = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: inherit;
  font-weight: 500;
  line-height: inherit;
`;

// ----- ViewBody / ViewContent (Lexical HTML 본문) -----

export const BodyRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  paddingTop: 8,
  [theme.breakpoints.down('md')]: {
    gap: 16,
    paddingTop: 4,
  },
}));

/* Lexical HTML 본문 렌더 영역 */
export const ContentRoot = styled(Box)`
  ${appDarkInvertedPaperCss}
  width: 100%;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  --lexical-default-font-size: 16px;
  line-height: 24px;
  color: var(--dk-text-strong, #555);
  word-break: break-word;

  /* 마지막 블록 아래 여백 제거 — 컨테이너와 밀착(뷰어 Wrap 과 동일).
     위 여백은 "* + 제목/인용" 규칙으로 앞 블록이 있을 때만 주므로 첫 블록은 자동으로 0. */
  & > *:last-child {
    margin-bottom: 0;
  }

  & p {
    margin: 0 0 16px;
  }
  /* 제목~코드 값은 전역 마크다운 기준(= 일반적인 마크다운 표기)과 동일 —
     기준: components/Record/TalkFLow/Markdown.tsx / public/css/lexical_editor.scss */
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0 0 16px;
    font-weight: 600;
    line-height: 1.25;
  }
  & * + h1,
  & * + h2,
  & * + h3,
  & * + h4,
  & * + h5,
  & * + h6 {
    margin-top: 24px;
  }
  & h1 {
    font-size: 2em;
  }
  & h2 {
    font-size: 1.5em;
  }
  & h3 {
    font-size: 1.25em;
  }
  & h4 {
    font-size: 1em;
  }
  & h5 {
    font-size: 0.875em;
  }
  /* h6 는 크기로만 구분 — 회색 글자는 GitHub 렌더링의 장식이라 쓰지 않는다. */
  & h6 {
    font-size: 0.85em;
  }
  & ul,
  & ol {
    margin: 0 0 16px;
    padding-left: 2em;
  }
  & li + li {
    margin-top: 0.25em;
  }
  & blockquote {
    margin: 0 0 16px;
    padding: 0 1em;
    border-left: 0.25em solid var(--dk-line-strong, #d1d9e0);
    color: var(--dk-text-strong, #59636e);
  }
  & pre,
  & code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  }
  & code {
    font-size: 0.85em;
  }
  & pre {
    margin: 0 0 16px;
    padding: 16px;
    background-color: var(--dk-bg-subtle, #f6f8fa);
    border-radius: 6px;
    overflow-x: auto;
    line-height: 1.45;
  }
  /* pre > code 는 바깥 pre 가 이미 배경·패딩을 갖는다(뷰어 pre code 와 동일).
     pre 안에서 0.85em 을 또 곱하지 않도록 1em 으로 되돌린다. */
  && pre code {
    padding: 0;
    background: none;
    font-size: 1em;
  }
  /* 인라인 코드만. Lexical 코드블록(.PlaygroundEditorTheme__code)은 좌측 52px 이
     줄번호 gutter 자리라 패딩을 덮으면 첫 글자가 gutter 에 가려진다. */
  & code:not(.PlaygroundEditorTheme__code) {
    padding: 0.2em 0.4em;
    background-color: rgba(129, 139, 152, 0.12);
    border-radius: 6px;
  }
  /* 작성화면(.PlaygroundEditorTheme__table)·회의록 뷰어와 동일 —
     넓은 표는 셀을 줄바꿈하지 말고 가로 스크롤(Notion 방식). display:block 이라야 overflow 가 먹는다. */
  & table {
    border-collapse: collapse;
    margin: 0 0 16px;
    display: block;
    width: max-content;
    max-width: 100%;
    overflow-x: auto;
    /* block 표에서는 레거시 인라인 height 가 고정 높이로 굳어 아래 행이 잘림 */
    height: auto !important;
  }
  /* 리스트 든 콘텐츠 박스 표는 항목 한 줄 길이만큼 늘어나 불필요한 스크롤이 생긴다 → 폭 고정(작성화면과 동일). */
  & table:has(li) {
    width: 100%;
  }
  & table th,
  & table td {
    border: 1px solid var(--dk-line-strong, #d1d9e0);
    padding: 6px 13px;
    vertical-align: top;
  }
  /* colgroup 이 있는 표는 colgroup 만 열 폭의 권위 — 셀 인라인 width 는 무력화.
     엑셀 붙여넣기가 td 에 심은 width 가 저장 HTML 에 남아, 편집화면에서 좁힌 열이
     조회화면에서 엑셀 원본 폭으로 되돌아감(48px 로 줄인 열이 112px 로 표시).
     colgroup 이 없는 표(레거시 v1·HTML 메일)는 td width 가 유일한 폭 정보라 제외.

     :has(colgroup) / 자손 td 로 쓰면 중첩 표에서 양방향으로 새어 제외가 깨짐 —
     ① colgroup 있는 바깥 표의 자손이라 안쪽 표 td 까지 걸림
     ② :has() 도 자손 판정이라 안쪽에만 colgroup 이 있으면 바깥 표까지 매칭됨.
     그래서 :has(> colgroup) + 직속 경로로 자기 표의 셀만 지정할 것. */
  & table:has(> colgroup) > tr > th,
  & table:has(> colgroup) > tr > td,
  & table:has(> colgroup) > thead > tr > th,
  & table:has(> colgroup) > thead > tr > td,
  & table:has(> colgroup) > tbody > tr > th,
  & table:has(> colgroup) > tbody > tr > td,
  & table:has(> colgroup) > tfoot > tr > th,
  & table:has(> colgroup) > tfoot > tr > td {
    width: auto !important;
  }
  /* 모바일: 셀 글자가 아무 데서나 꺾이면(문단 overflow-wrap:anywhere) 표가 화면 폭까지 줄어
     가로 스크롤이 안 생김 → 단어 단위로만 꺾음 */
  @media screen and (max-width: 768px) {
    & table th,
    & table td,
    & table th *,
    & table td * {
      word-break: keep-all;
      overflow-wrap: break-word;
    }
  }
  /* 헤더 행은 옅은 배경 + 굵게로 구분한다(Notion 방식). 짝수행 줄무늬는 쓰지 않는다. */
  & table th {
    background-color: var(--dk-bg-subtle, #f6f8fa);
    font-weight: 600;
  }
  /* 셀 안 문단의 아래 여백이 셀 높이를 밀어내지 않게. */
  & table th > :last-child,
  & table td > :last-child {
    margin-bottom: 0;
  }
  & a {
    color: var(--dk-accent, #0066cc);
    text-decoration: underline;
    word-break: break-all;
  }
  & a:hover {
    color: var(--dk-accent, #004c99);
  }
  & img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }
  & hr {
    height: 1px;
    padding: 0;
    border: 0;
    background-color: var(--dk-bg-strong, #d1d9e0);
    margin: 0 0 24px;
  }
  & * + hr {
    margin-top: 24px;
  }
  & input[disabled],
  & input[readonly] {
    cursor: default;
    opacity: 1;
  }
  & input[type='checkbox'][disabled],
  & input[type='radio'][disabled] {
    cursor: default;
  }
`;

/* 본문 인라인 이미지 "크게보기" 우하단 확대 아이콘 */
export const ZoomExpandIcon = styled('span')`
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 4px;
  background: rgba(240, 240, 240, 0.8);
  color: var(--dk-text-strong, #555);
  pointer-events: none;
  transition: background 0.15s ease, color 0.15s ease;

  & svg {
    width: 18px;
    height: 18px;
  }
`;

/* 본문 인라인 이미지 "크게보기" wrapper */
export const ZoomImageWrap = styled('span')`
  position: relative;
  display: inline-block;
  line-height: 0;
  max-width: 100%;
  cursor: pointer;

  & > img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }

  &:hover .expand-icon {
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
  }
`;

export const InlineImagesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12,
  marginTop: 16,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 8,
  },
  '@media screen and (max-width: 400px)': {
    gridTemplateColumns: '1fr',
  },
}));

export const InlineImageBox = styled('a')`
  display: block;
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 6px;
  overflow: hidden;
  background-color: var(--dk-bg-muted, #f3f5f6);
  border: 1px solid var(--dk-line, #e7e9ec);
  cursor: zoom-in;
  text-decoration: none;
  color: inherit;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: var(--dk-accent-line, #c2daf3);
  }
  &:focus-visible {
    outline: 2px solid var(--dk-accent, #0066cc);
    outline-offset: 2px;
  }
`;

// ----- AttachmentsReadOnly -----

export const AttachmentsRoot = styled(Box)`
  display: flex;
  flex-direction: column;
`;

// ----- 사이드 섹션 셸 (2단계에서 PHP 톤으로 재작업 시 일부 폐기/교체 예정) -----

export const SideSectionBox = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const SideSectionTitle = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--dk-text, #1d2025);
  user-select: none;
`;

export const SideSectionTitleLabel = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const SideSectionTitleRight = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--dk-accent, #0066cc);
`;

export const SideSectionTitleMeta = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--dk-accent, #0066cc);
`;

export const SideSectionTitleChevron = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: var(--dk-accent, #0066cc);
`;

export const SideSectionContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
`;

// ----- ApproverAvatar -----

export const ApproverAvatarRoot = styled(Box)`
  border-radius: 999px;
  overflow: hidden;
  background-color: var(--dk-bg-muted, #f3f5f6);
  flex-shrink: 0;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// ----- ApproverCard -----

export const ApproverCardRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'variant' && p !== 'isCurrent',
})<{ variant?: 'single' | 'cooperation'; isCurrent?: boolean }>(
  ({ variant = 'single' }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    backgroundColor: variant === 'cooperation' ? 'transparent' : '#fff',
    border: variant === 'cooperation' ? 'none' : '1px solid transparent',
    borderRadius: 6,
    minWidth: 0,
  }),
);

export const ApproverNumberHead = styled(Box, {
  shouldForwardProp: (p) => p !== 'variant',
})<{ variant?: 'single' | 'cooperation' }>(({ variant = 'single' }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 999,
  border: `1px solid ${variant === 'cooperation' ? '#c2daf3' : '#e7e9ec'}`,
  backgroundColor: variant === 'cooperation' ? '#f4f9ff' : '#fff',
  color: 'var(--dk-text-primary, #34383d)',
  fontFamily: 'Pretendard, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '16px',
  '& > span': {
    display: 'inline-block',
  },
}));

export const ApproverCardBody = styled(Box)`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ApproverCardNameRow = styled(Box)`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
`;

export const ApproverCardName = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--dk-text, #1d2025);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ApproverCardPosition = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: var(--dk-text-secondary, #888d96);
  flex-shrink: 0;
`;

export const ApproverCardDept = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: var(--dk-text-secondary, #888d96);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ApproverCardStatusSlot = styled(Box)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`;

export const ApprovalLineList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// ----- 협조결재 그룹 -----

export const CoApprovalGroupRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--dk-line, #e0e7f0);
  border-radius: 8px;
  background-color: var(--dk-bg-subtle, #fbfcfe);
  overflow: hidden;
`;

export const CoApprovalGroupHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--dk-line, #e0e7f0);
  background-color: var(--dk-bg-subtle, #f4f7fc);
`;

export const CoApprovalGroupHeaderLeft = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const CoApprovalGroupHeaderIcon = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #4d72a8;
`;

export const CoApprovalGroupHeaderTitle = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: #4d72a8;
`;

export const CoApprovalGroupOrderLabel = styled(FormControlLabel)`
  margin-left: 0;
  margin-right: 0;
  & .MuiFormControlLabel-label {
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: var(--dk-text-primary, #34383d);
    margin-left: 4px;
  }
`;

export const CoApprovalGroupBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 6px 8px;
`;

// ----- 참조인 pill -----

export const ReferrerPillList = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ReferrerPill = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px 0 4px;
  border-radius: 999px;
  background-color: var(--dk-bg-muted, #f3f5f6);
  min-width: 0;
  max-width: 100%;

  &:hover {
    background-color: var(--dk-bg-strong, #e7e9ec);
  }
`;

export const ReferrerPillName = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: var(--dk-text-primary, #34383d);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ReferrerAddButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 10px;
  border: 1px dashed var(--dk-line-strong, #d4d7dc);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--dk-text-strong, #4d5157);
  transition: background-color 120ms ease, border-color 120ms ease,
    color 120ms ease;

  &:hover {
    background-color: var(--dk-bg-subtle, #fafafa);
    border-color: #888d96;
    color: var(--dk-text, #1d2025);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// ----- CategoryBreadcrumb -----

/* 브래드크럼 첫 줄 wrapper */
export const CategoryBreadcrumbRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  background-color: var(--dk-bg, #fff);
  min-width: 0;
`;

/* 브래드크럼 좌측 영역 — dropdown 이 밖으로 펼쳐져야 해서 overflow hidden 금지. 모바일 미노출 */
export const BreadcrumbInner = styled(Box)`
  display: none;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  gap: 0;
  overflow: visible;

  @media (min-width: 576px) {
    display: flex;
  }
`;

/* 부모/본문 chip 내부 텍스트 — 한 줄 초과 시 말줄임 */
export const BreadcrumbText = styled('span')`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

/* 부모 품의가 복수일 때 노출되는 '+N' 카운터 */
export const ExtraCountBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 24px;
  text-align: center;
  color: var(--dk-accent, #0066cc);
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
`;

/* 부모/본문 chip 사이 separator */
export const BreadcrumbSeparator = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin: 0 10px;
  color: var(--dk-text, #000);

  & > svg {
    width: 12px;
    height: 12px;
  }
`;

/* 부모/본문 chip 공통 */
const chipStyleFactory = (variant: 'parent' | 'child' = 'parent') => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 0,
  flex: '0 1 auto',
  padding: 0,
  background: 'transparent',
  color: variant === 'child' ? '#000' : '#888d96',
  fontFamily: 'Pretendard, sans-serif',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '24px',
  border: 'none',
  cursor: 'inherit',
});

export const CategoryChip = styled('span', {
  shouldForwardProp: (p) => p !== 'variant' && p !== 'truncated',
})<{ variant?: 'parent' | 'child'; truncated?: boolean }>(
  ({ variant = 'parent', truncated }) => ({
    ...chipStyleFactory(variant),
    ...(truncated && { maxWidth: 'calc((100% - 32px) / 2)' }),
  }),
);

/* 클릭 가능한 parent chip */
export const CategoryChipButton = styled('button', {
  shouldForwardProp: (p) => p !== 'variant',
})<{ variant?: 'parent' | 'child' }>(({ variant = 'parent' }) => ({
  ...chipStyleFactory(variant),
  gap: 5,
  padding: 2,
  lineHeight: '20px',
  borderRadius: 4,
  width: 'fit-content',
  maxWidth: '100%',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background-color 0.3s ease',
  // 이름 텍스트(첫 자식)만 좌측 padding 2 추가 — hover bg 좌측 여백 확보.
  '& > span:first-of-type': {
    paddingLeft: 2,
  },
  '&:hover, &:focus, &:focus-visible, &:active, &[aria-expanded="true"]': {
    backgroundColor: 'var(--dk-bg-muted, #f3f5f6)',
    outline: 'none',
  },
}));

export const BreadcrumbIconWrap = styled('span', {
  shouldForwardProp: (p) => p !== 'variant',
})<{ variant?: 'parent' | 'child' }>(({ variant = 'parent' }) => {
  const color = variant === 'child' ? '#0066cc' : '#888d96';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    padding: 2,
    borderRadius: 2,
    boxSizing: 'border-box',
    backgroundColor:
      variant === 'child'
        ? 'rgba(0, 102, 204, 0.12)'
        : 'rgba(136, 141, 150, 0.12)',
    flexShrink: 0,
    color,
    '& > svg': {
      display: 'block',
      width: 12,
      height: 12,
      color,
    },
    '& > svg path, & > svg circle, & > svg rect': {
      stroke: color,
    },
  };
});

// ----- StatusFormLabels -----

/* 상태 chip + "양식: 기본품의" */
export const StatusFormRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
`;

/** 헤더 상태 chip */
export const StatusChipPink = styled('span', {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone?: StatusBadgeTone }>(({ tone = 'info' }) => {
  const palette = APPROVAL_HEADER_CHIP[tone];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: palette.bg,
    color: palette.color,
    fontFamily: 'Pretendard, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  };
});

/* "양식: 기본품의" inline 라벨 */
export const FormInlineLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background-color: var(--dk-bg-muted, #efefef);
  color: var(--dk-text-strong, #666);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
  box-sizing: border-box;
`;

// ----- TitleMetaBar -----

/** noBorder: 다음에 오는 DocumentActions row 가 divider 를 제공할 때 보더 생략 */
export const TitleBlock = styled(Box, {
  shouldForwardProp: (p) => p !== 'noBorder',
})<{ noBorder?: boolean }>(({ noBorder }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '20px 0 8px 0',
  borderBottom: noBorder ? 'none' : '1px solid #e7e9ec',
}));

/**
 * 작성자 메타 + 우측 아이콘 바 배치.
 * PC: [이름·직급 소속 | 날짜] ...... [아이콘] 한 줄
 * MO(<=768px): 1줄 [이름·직급 소속] / 2줄 [날짜] ...... [아이콘]
 * breakpoint 는 ActionMenu·TranslationMenu 의 MOBILE_MEDIA_QUERY 와 동일 유지.
 */
export const TitleMetaIconRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
`;

/*  큰 제목 */
export const TitleText = styled('h1')`
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: var(--dk-text-strong, #555);
  word-break: break-word;
`;

/* 메타 1행 — 이름·직급 + 소속. 세 값이 서로 갈라지지 않도록 nowrap 고정 */
export const MetaRow = styled(Box)`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
`;

/*
 * 메타 2행 — 날짜 + 우측 아이콘 바. PC 에서는 메타 1행과 같은 줄의 오른쪽 영역.
 * 내용물(날짜 nowrap, 아이콘 flex-shrink 0)이 축소 불가라 이 행도 축소 대상에서 제외한다.
 * 축소를 허용하면 좁은 PC 폭 + 장문 소속에서 아이콘이 컨테이너 밖으로 밀린다.
 */
export const MetaDateIconRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 auto;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
`;

export const MetaText = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--dk-text-dim, #b7bbc2);
  white-space: nowrap;
`;

/** 소속 — 폭이 모자라면 이 항목만 줄어들며 말줄임 (이름·직급은 유지) */
export const MetaDepartment = styled(MetaText)`
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 이름 + 직급 */
export const MetaName = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  color: var(--dk-text, #000);
  font-weight: 500;
  white-space: nowrap;
`;

/** 날짜 앞 구분자. MO 에서는 날짜가 아래 줄로 내려가 구분자가 불필요 */
export const MetaSep = styled('span')`
  display: inline-block;
  color: var(--dk-text-disabled, #d4d7dc);
  user-select: none;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const TitleIconBar = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: auto;
`;

/* 제목 우측 5개 아이콘 (번역/공유/즐겨찾기/코멘트/더보기) */
export const TitleIconButton = styled('button', {
  shouldForwardProp: (p) => p !== 'marked',
})<{ marked?: boolean }>(({ marked }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: 6,
  color: marked ? '#c77ac4' : '#888d96',
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover': {
    backgroundColor: marked ? 'rgba(199, 122, 196, 0.08)' : '#f3f5f6',
  },
  '&:focus-visible': {
    outline: '2px solid #000',
    outlineOffset: 2,
  },
}));

/**
 * 「AI 어시스턴트」 버튼 — 아이콘 바 맨 앞의 핑크 필(디자인 목업 값). 패널이 열려 있으면 marked.
 * 모바일(≤768px)은 라벨을 숨기고 아이콘만 남긴다.
 */
export const AiAssistantButton = styled('button', {
  shouldForwardProp: (p) => p !== 'marked',
})<{ marked?: boolean }>(({ marked }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  padding: '0 10px',
  marginRight: 4,
  border: '1px solid var(--dk-purple-line, #ebd6ea)',
  borderRadius: 8,
  background: marked
    ? 'var(--dk-purple-soft, #f5e7f4)'
    : 'var(--dk-purple-soft, #faf3fa)',
  color: 'var(--dk-purple-text, #a75ba4)',
  fontFamily: 'inherit',
  fontSize: 13,
  lineHeight: '20px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '& > svg': { width: 14, height: 14, flexShrink: 0 },
  '&:hover': {
    background: 'var(--dk-purple-soft, #f5e7f4)',
  },
  '&:focus-visible': {
    outline: '2px solid #c77ac4',
    outlineOffset: 2,
  },
  '@media (max-width: 768px)': {
    padding: '0 6px',
    '& > span': { display: 'none' },
  },
}));

/** 코멘트 아이콘 버튼 — 카운트 텍스트가 붙어 너비 가변 */
export const CommentIconButton = styled(TitleIconButton)`
  width: auto;
  padding: 0 6px;
  gap: 4px;
`;

/** 코멘트 카운트 텍스트 */
export const CommentCountText = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  color: var(--dk-text-secondary, #888d96);
`;

// ----- DocumentActions (편집 / 문서삭제 버튼) -----

/** 문서 레벨 액션 버튼 row — 반려(R)/임시저장(S)에서만 노출 (consumer 분기) */
export const DocActionRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
`;

/* "편집" 버튼 */
export const DocEditButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--dk-line-strong, #c9ccd1);
  border-radius: 4px;
  background-color: var(--dk-bg, #fff);
  color: var(--dk-text-strong, #555);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  & svg {
    width: 14px;
    height: 14px;
    color: var(--dk-text-strong, #555);
  }
  &:hover {
    background-color: var(--dk-bg-muted, #f3f5f6);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

/** "의견입력 결재, 반려" 버튼 */
export const DocActionTriggerButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  margin-left: auto;
  padding: 0 12px;
  border: 1px solid #2a2a2a;
  border-radius: 4px;
  background-color: #2a2a2a;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  & svg {
    width: 14px;
    height: 14px;
    color: #fff;
  }
  &:hover {
    background-color: #000;
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

/** "문서삭제" 버튼 */
export const DocDeleteButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${APPROVAL_STATUS_COLOR.danger};
  border-radius: 4px;
  background-color: ${APPROVAL_STATUS_COLOR.danger};
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  & svg {
    width: 14px;
    height: 14px;
    color: #fff;
  }
  &:hover {
    filter: brightness(0.92);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// ----- ApprovalLineSlot (PHP iframe 톤 카드) -----

/** "결재라인" wrapper */
export const ApprovalLineSectionLabel = styled('div')`
  margin: 8px 0 0 0;
  display: inline-block;
  align-self: flex-start;
  width: auto;
  padding: 4px 8px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background-color: var(--dk-bg-muted, #efefef);
  color: var(--dk-text-strong, #666);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
  box-sizing: border-box;
`;

/** 결재라인을 숨기는 문서에서 제목 메타 보더와 다음 슬롯 사이 간격을 결재라인 있는 문서와 맞춤 */
export const HiddenApprovalLineSpacer = styled('div')`
  height: 12px;
`;

/** 결재라인 카드 가로 wrap */
export const ApprovalLineRow = styled(Box, {
  shouldForwardProp: (p) => p !== 'noBorder',
})<{ noBorder?: boolean }>(({ noBorder }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  // 카드 폭을 화면이 아니라 이 줄(결재 화면이 놓인 칸) 폭으로 정한다 — 앱 분할 상세(iPhone Duo · iPad) 대응
  containerType: 'inline-size',
  padding: noBorder ? 0 : '0 0 12px',
  marginBottom: noBorder ? 0 : 12,
  borderBottom: noBorder ? 'none' : '1px solid #e7e9ec',
}));

/** 결재라인 카드 — currentTone(info/warning)만 보더 강조, 반려는 보더 대신 하단 딱지만 부각 */
export const ApprovalLineCardRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'currentTone',
})<{ currentTone?: StatusBadgeTone }>(({ currentTone }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  // grow 를 열어두면 행마다 카드 수에 따라 폭이 달라짐 — 고정 폭 유지
  flex: '0 0 250px',
  minWidth: 126,
  maxWidth: '100%',
  padding: '20px 8px 0 8px',
  border:
    currentTone === 'info' || currentTone === 'warning'
      ? `1px solid ${APPROVAL_STATUS_COLOR[currentTone]}`
      : '1px solid #e5e6e9',
  borderRadius: 4,
  backgroundColor: 'var(--dk-bg, #fff)',
  overflow: 'hidden',
  fontFamily: 'Pretendard, sans-serif',
  fontSize: 16,
  boxSizing: 'border-box',
  // 250px 카드 3장(250×3 + 간격 16 = 766px)이 안 들어가는 칸이면 한 줄 3장으로 줄인다 — 1장씩 세로로 쌓이던 것
  '@container (max-width: 765px)': {
    flexBasis: 'calc((100% - 16px) / 3)',
    minWidth: 0,
  },
  // 폰(화면 480px 이하)은 지금처럼 한 줄 2장 — 위 규칙보다 뒤에 둬야 이긴다
  '@media (max-width: 480px)': {
    flexBasis: 'calc(50% - 4px)',
  },
}));

/* 카드 우상단 [순번] chip */
export const ApprovalLineCardOrderChip = styled('span', {
  shouldForwardProp: (p) => p !== 'currentTone',
})<{ currentTone?: StatusBadgeTone }>(({ currentTone }) => {
  const isHighlighted = currentTone === 'info' || currentTone === 'warning';
  return {
    position: 'absolute',
    top: -1,
    right: -1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    padding: 0,
    backgroundColor: isHighlighted
      ? APPROVAL_STATUS_COLOR[currentTone]
      : '#dfe0e4',
    color: isHighlighted ? '#fff' : '#666',
    fontFamily: 'Pretendard, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
    boxSizing: 'border-box',
  };
});

/** 부서명 row */
export const ApprovalLineCardHeader = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Pretendard, sans-serif;
  font-size: 0.9em;
  font-weight: 400;
  line-height: 18px;
  color: var(--dk-text-strong, #616161);
  letter-spacing: -0.03em;
`;

export const ApprovalLineCardHeaderDept = styled('span')`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** 직급 row */
export const ApprovalLineCardHeaderPosition = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Pretendard, sans-serif;
  font-size: 0.9em;
  font-weight: 400;
  line-height: 1.3em;
  color: var(--dk-text-strong, #616161);
  margin-top: 3px;
`;

/** 이름 row */
export const ApprovalLineCardName = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 0 8px 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--dk-text-strong, #616161);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

/** 카드 하단 status 영역 wrapper */
export const ApprovalLineCardStatus = styled(Box)`
  width: 100%;
  min-height: 36px;
  padding: 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--dk-line, #e5e6e9);
  box-sizing: border-box;
`;

/** 상태 chip — tone 별 배경/글자색 분기 */
export const ApprovalLineCardStatusChip = styled('span', {
  shouldForwardProp: (p) => p !== 'tone' && p !== 'isCompleted',
})<{ tone: StatusBadgeTone; isCompleted?: boolean }>(
  ({ tone, isCompleted }) => {
    const baseChip = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      margin: 0,
      fontFamily: 'Pretendard, sans-serif',
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '18px',
    } as const;

    if (isCompleted) {
      return {
        ...baseChip,
        backgroundColor: 'transparent',
        color: 'var(--dk-text-strong, #555)',
      };
    }

    if (tone === 'neutral') {
      return {
        ...baseChip,
        backgroundColor: 'transparent',
        color: 'var(--dk-text-secondary, #999)',
        fontSize: 16,
      };
    }

    // 결재 도메인 chip 토큰. neutral 은 chip 자체 비표시
    const palette = APPROVAL_STATUS_CHIP[tone];
    return {
      ...baseChip,
      backgroundColor: palette.bg,
      color: palette.color,
      padding: '4px 8px',
      borderRadius: 4,
    };
  },
);

// ----- 서버 HTML 본문 공용 규칙 -----

/**
 * 서버가 내려주는 의견/로그 본문 HTML 의 블록 여백 규칙 — 의견 박스(OpinionBoxBody)와
 * 코멘트 로그 행(LogContent)이 같은 HTML 을 렌더하므로 두 곳이 공유한다.
 * 마진은 아래쪽만 주고 마지막 블록에서 지우므로 한 줄짜리 본문은 높이가 그대로다.
 * 줄바꿈은 <br> 담당이라 white-space: pre-line 금지(소스 개행 이중 줄바꿈).
 */
const serverHtmlBlockSpacing = css`
  /* 마진 규칙은 && — 코멘트 영역(CommentRoot)이 .PlaygroundEditorTheme__paragraph 의
     마진을 클래스 2개 특이도로 덮고 있어, & 하나로는 로그 행에서 문단 여백이 먹지 않는다. */
  && p {
    margin: 0 0 12px;
  }
  /* 에디터에서 빈 줄로 입력된 <p><br></p> — 문단 여백과 겹쳐 두 배로 벌어지고,
     본문 끝의 빈 줄은 박스 아래 빈 공간이 된다. 빈 줄은 접고 문단 여백만 남긴다. */
  && p:empty,
  && p:has(> br:only-child) {
    margin: 0;
    height: 0;
    overflow: hidden;
  }
  /* 최상위 목록에만 — 자식 결합자를 쓰지 않으면 전역 SCSS 의 중첩 목록 규칙
     (li > ul/ol 은 0.25em 으로 바깥 항목에 붙임)까지 덮어 버린다. */
  && > ul,
  && > ol {
    margin: 0 0 12px;
    padding-left: 2em;
  }
  && li + li {
    margin-top: 0.25em;
  }
  & img {
    max-width: 100%;
    height: auto;
  }
  /* 마지막 블록만 아래 여백 0 — 위 마진 규칙들과 특이도가 같으므로 뒤에 와야 이긴다.
     덕분에 한 줄짜리 본문은 여백 없이 박스 높이가 그대로다. */
  && > *:last-child {
    margin-bottom: 0;
  }
`;

// ----- ReturnRequestNotice (반려요청 안내) -----

/**
 * 반려요청 안내 — 요청받은 현재 결재자와 기안자에게만 보이는 알림 성격의 박스.
 *
 * 결재자 의견 박스(반려=빨강)와 색·자리를 일부러 다르게 둔다. 반려요청은 결재 결과가 아니라
 * "반려해 달라"는 부탁이고 노출 대상도 제한되므로, 결과 의견처럼 읽히면 안 된다.
 * 보류와 같은 amber 계열을 쓰되 테두리를 더 진하게 잡아 알림임을 드러낸다.
 */
export const ReturnRequestNoticeRoot = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0;
  padding: 12px 16px;
  border: 1px solid rgba(254, 174, 0, 0.56);
  border-radius: 8px;
  background-color: rgba(254, 174, 0, 0.08);
  font-family: Pretendard, sans-serif;
`;

/** 좌측 경고 아이콘 — svg 가 stroke: currentColor 라 color 로 색을 준다. */
export const ReturnRequestNoticeIcon = styled('span')`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 1px;
  color: #d99000;

  & svg {
    width: 18px;
    height: 18px;
  }
`;

export const ReturnRequestNoticeBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const ReturnRequestNoticeTitle = styled('span')`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--dk-text, #131313);
`;

/**
 * 노출 대상 안내 — 모두에게 보이는 결재자 의견과 혼동되지 않게 항상 함께 띄운다.
 * 사유보다 뒤로 물러나야 해 한 단계 작고 흐린 글자를 쓴다.
 */
export const ReturnRequestNoticeHint = styled('p')`
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--dk-text-secondary, #888d96);
`;

/** 사유 본문 — 사용자가 줄바꿈을 넣을 수 있어 pre-wrap. HTML 이 아니라 평문으로 렌더한다. */
export const ReturnRequestNoticeReason = styled('p')`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, #131313);
  white-space: pre-wrap;
  word-break: break-word;
`;

// ----- OpinionSlot (결재자 의견) -----

/** 결재자 의견 섹션 래퍼 — 상단선은 위 결재라인 섹션이 담당, 하단 구분선만 */
export const OpinionSectionRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  /* 박스 사이 12px — 8px 이면 발언자가 바뀌는 지점이 본문 문단 간격에 묻힌다. */
  gap: 12px;
  margin: 4px 0 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
`;

/** 섹션 헤더 — pill 아닌 plain bold 텍스트. */
export const OpinionSectionLabel = styled('div')(infoSectionTitleStyles);

/** 결재자 의견 박스 — 코멘트 로그 행(LogRoot)과 동일 톤(APPROVAL_LOG_TONE) 재사용. */
export const OpinionBoxRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone: ApprovalLogToneStyle }>(({ tone }) => {
  // 코멘트 로그 행(LogRoot)과 100% 동일 톤 — border/bg 가 status 색 전담, 본문은 다크.
  const palette = APPROVAL_LOG_TONE[tone];
  return {
    padding: 16,
    borderRadius: 4,
    border: `1px solid ${palette.border}`,
    backgroundColor: palette.bg,
    color: 'var(--dk-text, #131313)',
    fontFamily: 'Pretendard, sans-serif',
    fontSize: 14,
    lineHeight: '20px',
  };
});

/** 의견 박스 헤더 — 이름(좌) + 타임스탬프(우). 의견 박스 tone 의 text color 상속. */
export const OpinionBoxHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  line-height: 20px;
`;

export const OpinionBoxAuthor = styled('span')`
  font-weight: 600;
`;

export const OpinionBoxTimestamp = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-weight: 400;
  color: var(--dk-text-secondary, #888d96);

  & svg {
    width: 14px;
    height: 14px;
  }
`;

/**
 * 의견 본문 — 블록 여백은 serverHtmlBlockSpacing 공유.
 * line-height 는 박스(OpinionBoxRoot)의 20px 을 덮는다 — 16px 본문에 20px 은 좁다.
 * --lexical-default-font-size 는 본문 HTML 의 Lexical 클래스(문단/목록 항목)가 따르는 값 —
 * 미지정이면 전역 SCSS 의 fallback 18px 이 걸려 목록 항목만 커진다.
 */
export const OpinionBoxBody = styled(Box)`
  ${appDarkRichContentCss}
  white-space: normal;
  word-break: break-word;
  font-size: 16px;
  --lexical-default-font-size: 16px;
  line-height: 24px;

  ${serverHtmlBlockSpacing}
`;

// ----- AiSummarySlot -----

/** AI 요약 박스 */
export const AiSummaryBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0 0 12px;
  padding: 20px;
  border-radius: 8px;
  background: linear-gradient(
        var(--dk-bg-subtle, #f9fafb),
        var(--dk-bg-subtle, #f9fafb)
      )
      padding-box,
    linear-gradient(
        135deg,
        rgba(225, 179, 223, 1),
        rgba(175, 178, 244, 1),
        rgba(225, 179, 223, 1)
      )
      border-box;
  border: 1px solid transparent;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 16px;
  }
`;

/** AI 요약 header row */
export const AiSummaryHeader = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
`;

/** chip 옆 안내 메시지 (no-content/error/idle) */
export const AiSummaryMessage = styled('p')`
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #c77ac4;
`;

/** "AI 요약" chip */
export const AiSummaryChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(199, 122, 196, 0.12);
  border: 1px solid var(--dk-purple-line, #f8eff8);
  color: #c77ac4;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  flex-shrink: 0;
  box-sizing: border-box;

  & > svg {
    color: #c77ac4;
  }
`;

/**
 * chip 아래 본문 컨테이너
 *  - 문서 본문과 동일한 16px/24px. (요약이 본문보다 작게 보이던 문제 해소)
 *  - pre-wrap 유지 → 백엔드가 보낸 개행/들여쓰기 그대로 보존.
 *  - 폭 제한은 두지 않는다. 읽기 폭을 좁히면 박스는 full-width 인데 텍스트만 중간에서
 *    끊겨 우측이 비어 보인다. 박스 폭을 그대로 채우게 둔다.
 */
export const AiSummaryBody = styled('pre')`
  margin: 0;
  padding: 0;
  border: 0;
  width: 100%;
  background: transparent;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--dk-text, #000);
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:not(:empty) {
    margin-top: 12px;
  }
`;

/**
 * 요약 본문의 한 줄 ('- [항목] 내용' 단위)
 *
 * 요약은 개행으로 구분된 여러 항목으로 오는데, 순수 pre-wrap 만으로는 항목 사이에
 * 간격을 줄 수 없어 한 덩어리로 읽힌다. 줄 단위 block 으로 감싸 항목 간 간격을 준다.
 * 각 줄에도 pre-wrap 을 걸어 줄 앞 들여쓰기와 자동 줄바꿈 동작은 그대로 유지.
 */
export const AiSummaryLine = styled('span')`
  display: block;
  white-space: pre-wrap;

  & + & {
    margin-top: 12px;
  }
`;

/** chip 안 retry 버튼 (refresh 아이콘) */
export const AiSummaryChipButton = styled('button')`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  margin: 0;
  color: #c77ac4;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  /* all: unset 으로 사라진 키보드 포커스 표시 복구 (a11y) */
  &:focus-visible {
    outline: 2px solid #c77ac4;
    outline-offset: 2px;
  }
`;

const aiSummarySpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/** chip 안 inline spinner */
export const AiSummarySpinner = styled('span')`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1.5px solid rgba(199, 122, 196, 0.3);
  border-top-color: #c77ac4;
  border-radius: 50%;
  animation: ${aiSummarySpin} 0.8s linear infinite;
  box-sizing: border-box;
`;

// ----- BodySlot -----

/** 본문 슬롯 외곽 */
export const BodySlotRoot = styled(Box)`
  padding: 12px 0;
`;

// ----- AttachmentsSlot wrapper -----

export const AttachmentsSlotRoot = styled(Box)`
  margin: 12px 0;
`;

/** 첨부 영역 */
export const AttachmentsTable = styled('table')`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

export const AttachmentsTableHeadRow = styled('tr')`
  background-color: var(--dk-bg-muted, #e5e5e5);
`;

export const AttachmentsTableBodyRow = styled('tr')`
  background-color: var(--dk-bg, #ffffff);

  &:nth-of-type(even) {
    background-color: var(--dk-bg-muted, #eeeeee);
  }
`;

export const AttachmentThumbCell = styled('th')`
  padding: 8px;
  text-align: center;
  vertical-align: middle;

  tbody & {
    padding: 4px 8px;
  }
`;

export const AttachmentNameCell = styled('th')`
  padding: 8px;
  text-align: center;
  vertical-align: middle;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--dk-text-strong, #4d5157);

  tbody & {
    padding: 4px 8px;
    font-weight: 400;
    color: var(--dk-text, #1d2025);
  }
`;

export const AttachmentDownCell = styled('th')`
  padding: 8px;
  text-align: center;
  vertical-align: middle;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--dk-text-strong, #4d5157);

  tbody & {
    padding: 4px 8px;
    text-align: right;
    font-weight: 400;
    color: var(--dk-text, #1d2025);
  }
`;

export const AttachmentRowName = styled('a')`
  color: var(--dk-text, #1d2025);
  text-decoration: none;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;

  &:hover {
    color: var(--dk-accent, #0066cc);
    text-decoration: underline;
  }

  & > svg {
    margin-left: 6px;
    vertical-align: middle;
    color: var(--dk-text-secondary, #888d96);
  }
`;

/** 파일명 좌측 썸네일 — 이미지는 img, 비이미지는 확장자 아이콘 */
export const AttachmentThumb = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  overflow: hidden;

  & > img {
    width: 100%;
    height: auto;
    display: block;
  }

  & > svg {
    width: 24px;
    height: 24px;
  }
`;

export const AttachmentRowSize = styled('a')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--dk-text-secondary, #888d96);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  line-height: 18px;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: var(--dk-text-strong, #4d5157);
  }

  & > svg {
    flex-shrink: 0;
  }
`;

// ----- RelatedApprovalSlot -----

/** 연관품의 영역 카드 컨테이너 */
export const RelatedRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 12px 0;
  padding: 16px 12px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 8px;
  background-color: var(--dk-bg, #fff);

  @media (min-width: 576px) {
    padding: 16px;
  }
`;

export const RelatedHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

/** "연결된 품의 {N}건" 헤더 좌측 */
export const RelatedHeaderLeft = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--dk-text, #000);
`;

/** "{N}" 카운트 */
export const RelatedHeaderCount = styled('span')`
  font-weight: 600;
  color: var(--dk-text, #000);
  margin: 0 2px;
`;

/** 헤더 우측 — 변경이력 stub 버튼 + chevron 토글 버튼을 한 row 로 묶는 컨테이너 */
export const RelatedHeaderRight = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

/** "변경이력" 우측 텍스트 버튼 */
export const RelatedHeaderHistoryButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--dk-text-dim, #b7bbc2);
  text-decoration: underline;
  text-underline-position: from-font;

  &:hover {
    color: var(--dk-text-strong, #5f636b);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  & > svg {
    flex-shrink: 0;
    color: var(--dk-text-secondary, #888d96);
  }
`;

/** 여닫기 chevron 토글 버튼 */
export const RelatedHeaderToggleButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--dk-text-strong, #555);

  &:hover {
    color: var(--dk-text-primary, #333);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  & > svg {
    flex-shrink: 0;
    transition: transform 200ms ease;
  }
`;

/** parents/children/related 각 타입 블록 — 라벨 row + 카드 리스트 묶음 */
export const RelatedTypeBlock = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** 타입 라벨 row */
export const RelatedTypeLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--dk-text-strong, #4d5157);
  white-space: nowrap;
  box-sizing: border-box;

  & > svg {
    flex-shrink: 0;
  }
`;

/** 연결된 품의 카드 row chrome — bg/border/padding 은 공용 CardRowChrome 상속 */
export const ViewRowChrome = styled(CardRowChrome)`
  min-height: 36px;
`;

/* 본문 하단 버튼 영역 — 우측 정렬 */
export const RelatedFoot = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

/* 저장 버튼 라벨 — 저장 중 스피너로 바뀌어도 "저장" 폭 유지(레이아웃 시프트 방지) */
export const RelatedSaveLabel = styled('span')`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const RelatedSaveSpinner = styled('span')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* "연결된 품의가 없습니다." 빈 박스 */
export const RelatedEmpty = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--dk-text-dim, #b7bbc2);
  text-align: center;
`;

/** 우하단 "편집" 칩 */
export const RelatedEditButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 4px;
  background-color: var(--dk-bg, #fff);
  color: var(--dk-text-strong, #5f636b);
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: var(--dk-bg-subtle, #fafafa);
    border-color: var(--dk-line-strong, #d4d7dc);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// ----- CommentSlot -----

export const CommentRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  gap: 8px;

  /* lexical_editor.scss override */
  & .PlaygroundEditorTheme__paragraph {
    margin: 0;
    font-size: 14px;
    line-height: 22px;
  }
`;

export const CommentHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 4px 0;
  border-top: 1px solid var(--dk-line, #e7e9ec);
`;

/** "코멘트 {N}" 헤더 좌측 */
export const CommentHeaderLeft = styled('span')`
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--dk-text, #000);
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-thickness: 1px;
`;

/** 카운트 — 부모 underline/color/weight 그대로 상속 (별도 색·두께 override 없음). */
export const CommentHeaderCount = styled('span')``;

/** "↑↓ 최신순" 정렬 라벨 버튼 */
export const CommentSortLabel = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: var(--dk-text-strong, #555);

  & > svg {
    flex-shrink: 0;
  }

  &:hover {
    color: var(--dk-text-primary, #333);
    background: var(--dk-bg-muted, #f3f5f6);
  }
`;

// "코멘트 더보기" 텍스트 버튼
export const CommentMoreButton = styled(Button)`
  padding: 0;
  min-width: 0;
  display: block;
  text-align: left;
  background: transparent;

  &:hover {
    background: transparent;
    text-decoration: underline;
  }

  &:hover .MuiTypography-root {
    text-decoration: underline;
  }
`;

export const CommentList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;

  /* 본문 HTML 의 Lexical 클래스가 따르는 기준 글자 크기. 미지정이면 전역 SCSS 의
     fallback 18px 이 걸려, 문단만 CommentRoot 에서 14px 로 덮이고 목록 항목은 18px 로 남는다.
     작성 에디터(CommentInput)에 상속되지 않도록 CommentRoot 가 아닌 목록에만 선언한다. */
  --lexical-default-font-size: 14px;

  & .list-wrapper p {
    margin: 0;
  }

  & .MuiStack-root:has(.name-position) {
    margin-bottom: 4px;
  }
`;

// ----- ReadersSlot -----

export const ReadersRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 8px 0 12px;
  background-color: transparent;
`;

/** "이 문서를 읽은사람들 (읽음: ✓)" 라벨 */
export const ReadersHeading = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-text-strong, #555);
  margin-bottom: 4px;
`;

/** 라벨 안 ✓ 마크 */
export const ReadersHeadingCheck = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: #61b6d6;
`;

/** 표 (3 col) */
export const ReadersTable = styled(Box)`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--dk-line, #e7e9ec);

  /* row 2 (추가 참조인) bg 회색 */
  & > :nth-of-type(2) {
    background-color: var(--dk-bg-muted, #eee);
  }
`;

export const ReadersTableRow = styled(Box)`
  display: grid;
  grid-template-columns: 130px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  line-height: 18px;
`;

/* 라벨 셀 */
export const ReadersTableLabel = styled('span')`
  color: var(--dk-text-strong, #555);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
`;

/* 이름 셀 */
export const ReadersTableMembers = styled('span')`
  min-width: 0;
  color: var(--dk-text-strong, #555);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  word-break: break-all;
`;

/* 카운트 셀 */
export const ReadersTableCount = styled('span')`
  color: var(--dk-text-strong, #555);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
`;

/** 멤버 좌측 ✓ */
export const ReadersInlineCheck = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: #61b6d6;
  margin-right: 2px;
  vertical-align: -1px;
`;

export const ReadersAddRow = styled(Box)`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

/* 참조인 추가 버튼 */
export const ReadersAddButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid var(--dk-line, #dde4e6);
  border-radius: 4px;
  background-color: var(--dk-bg-muted, #ecf0f1);
  color: var(--dk-text-primary, #333);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  cursor: pointer;
  box-sizing: border-box;

  & > svg {
    color: var(--dk-text-primary, #333);
    flex-shrink: 0;
  }

  &:hover {
    background-color: #e1e7e9;
    border-color: #c9d2d4;
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// ----- BottomLabels -----

export const BottomRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 0 16px;
  background-color: transparent;
`;

/** 작성일/조회등급 chip */
export const BottomLabelChip = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 4px;
  background-color: var(--dk-bg-muted, #efefef);
  color: var(--dk-text-primary, #333);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
  box-sizing: border-box;
`;

// ===== Translation (Stage [2/3] — 번역 적용 시각 요소) =====

/** 상단 "AI 번역됨 (언어명)" 띠 (#translationStatus) */
export const TranslationStatusBar = styled(Box)`
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  /* 모달 본문 상단 패딩을 상쇄해 띠를 모달 최상단(헤더 바로 아래)에 붙임 */
  margin-top: calc(-1 * var(--modal-padding-top, 0px));
  /* 띠 아래 본문과의 간격 */
  margin-bottom: 30px;
  padding: 0 4px;
  color: rgba(199, 122, 196, 1);
  box-sizing: border-box;

  /* 분홍 배경만 컨테이너 전폭(좌우 패딩까지), 컨텐츠 폭 제한은 Inner box 가 담당.
     100vw 로 넓히면 모달 본문(뷰포트보다 좁고 overflow:auto)에서 넘쳐 가로 스크롤이 생김.
     전체화면 조회는 스크롤 래퍼 자체가 100vw·패딩 0 이라 변수 폴백 0 으로 전폭 유지 */
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: calc(-1 * var(--modal-padding-x, 0px));
    right: calc(-1 * var(--modal-padding-x, 0px));
    height: 100%;
    background-color: var(--dk-purple-soft, #fbf4fa);
  }
`;

/** 라벨/취소를 본문 컬럼 폭으로 캡 — expand 모달에서 양끝으로 벌어지지 않게 */
export const TranslationStatusBarInner = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  max-width: 1914px;
`;

/** 상태 띠 좌측 "AI 번역됨 (언어명)" 라벨 — chip 배경 없음 (디자인 = 텍스트만) */
export const TranslationStatusLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: rgba(199, 122, 196, 1);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  & > svg {
    flex-shrink: 0;
    color: rgba(199, 122, 196, 1);
  }
`;

/** 제목/본문 위 별도 줄로 노출되는 'Ai 번역됨' chip (.ai-translate-tag) */
export const TranslateBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 12px 4px 8px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  color: #c77ac4;
  border: 1px solid var(--dk-purple-line, #f8eff8);
  border-radius: 999px;
  background: rgba(199, 122, 196, 0.12);
  user-select: none;

  & > svg {
    flex-shrink: 0;
    color: #c77ac4;
  }
`;

/** 우측 "취소" 텍스트 링크 버튼 */
export const TranslationCancelButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #c77ac4;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid #c77ac4;
    outline-offset: 2px;
  }
`;

// ----- ApprovalLogRow (결재 액션 로그 카드) -----

const APPROVAL_LOG_TONE = {
  default: {
    border: 'var(--dk-line-strong, #D1D8E2)',
    bg: 'var(--dk-bg-subtle, #fafbfc)',
    chipColor: 'var(--dk-text-secondary, #888d96)',
    chipBorder: 'var(--dk-line, rgba(231,233,236,1))',
    chipBg: 'var(--dk-bg-muted, rgba(231,233,236,0.32))',
  },
  confirm: {
    border: 'rgba(67,177,111,1)',
    bg: 'rgba(67,177,111,0.1)',
    chipColor: '#43B16F',
    chipBorder: 'rgba(67,177,111,0.24)',
    chipBg: 'rgba(67,177,111,0.12)',
  },
  reject: {
    border: 'rgba(245,80,96,1)',
    bg: 'rgba(245,80,96,0.1)',
    chipColor: '#F55060',
    chipBorder: 'rgba(245,80,96,0.24)',
    chipBg: 'rgba(245,80,96,0.12)',
  },
  hold: {
    // 보류 chip 보더: 레거시 copy-paste 버그(`rgba(245,80,96,0.24)`) 회피 — 권장 amber 24%.
    border: 'rgba(254,174,0,1)',
    bg: 'rgba(254,174,0,0.1)',
    chipColor: 'rgba(254,174,0,1)',
    chipBorder: 'rgba(254,174,0,0.24)',
    chipBg: 'rgba(254,174,0,0.12)',
  },
} as const;

type ApprovalLogToneStyle = keyof typeof APPROVAL_LOG_TONE;

export const LogRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone: ApprovalLogToneStyle }>(({ tone }) => {
  const palette = APPROVAL_LOG_TONE[tone];
  return {
    border: `1px solid ${palette.border}`,
    backgroundColor: palette.bg,
    borderRadius: 4,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    fontFamily: 'Pretendard, sans-serif',
  };
});

export const LogHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  /* 모바일 폭에서 일시가 아랫줄로 내려감. 없으면 이름·문구가 한 칸에 밀려 글자 단위로 쪼개짐 */
  flex-wrap: wrap;
`;

export const LogHeaderLeft = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
`;

export const LogProfileImage = styled(Box)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  overflow: hidden;
  background-color: var(--dk-bg-muted, #f3f5f6);
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const LogNameText = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--dk-text, #131313);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
`;

export const LogTitleText = styled('span')`
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-text, #131313);
  /* 좁은 폭에서 줄바꿈 대상은 일시(LogCreatedAt) — 이 문구는 통째로 유지 */
  white-space: nowrap;
`;

export const LogChip = styled('span', {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone: ApprovalLogToneStyle }>(({ tone }) => {
  const palette = APPROVAL_LOG_TONE[tone];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 8px',
    borderRadius: 4,
    border: `1px solid ${palette.chipBorder}`,
    backgroundColor: palette.chipBg,
    color: palette.chipColor,
    fontFamily: 'Pretendard, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    minWidth: 48,
    textAlign: 'center',
    marginLeft: 4,
    whiteSpace: 'nowrap',
  };
});

export const LogCreatedAt = styled('span')`
  flex-shrink: 0;
  /* 아랫줄로 내려갔을 때 이름·본문(LogContent)과 같은 시작선 — 아바타 32 + 간격 8 */
  margin-left: 40px;
  font-family: Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: var(--dk-text-secondary, #888d96);
  white-space: nowrap;
`;

/**
 * 로그 행 본문 — 의견 박스와 같은 HTML 을 렌더하므로 블록 여백도 같은 규칙을 공유한다.
 * 글자 크기는 옆에 나란히 뜨는 일반 코멘트(14px)에 맞춘다.
 * --lexical-default-font-size 미지정이면 전역 SCSS fallback 18px 이 걸려
 * 목록 항목만 이 행의 본문보다 커진다 — 행의 글자 크기와 같게 고정한다.
 */
export const LogContent = styled(Box)`
  ${appDarkRichContentCss}
  padding-left: 40px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  --lexical-default-font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: var(--dk-text, #131313);
  white-space: normal;
  word-break: break-word;

  ${serverHtmlBlockSpacing}

  & a {
    color: var(--dk-accent, #06c);
    text-decoration: underline;
  }
`;
